import { useTranslation } from "react-i18next";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { FileQuestion, ImagePlus, Pencil, Play, Scissors, X } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import type { ManualMediaRef } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { DeferredActionButton } from "@/components/ui/deferred-action-button";
import type { AuthSession } from "../../../auth/keycloak.js";
import { ImageAnnotateEditor } from "./image-annotate/ImageAnnotateEditor.js";
import { ImageCollageEditor } from "./image-collage/ImageCollageEditor.js";
import { VideoTrimEditor } from "./video-trim/VideoTrimEditor.js";

/** The trim window a Save posts to the async cut command (the media KIND + everything else is derived server-side). */
export type CutVideoInput = { sourceFileId: number; startSeconds: number; durationSeconds: number; name?: string };

/**
 * A step's attached media (ADR-002 Phase 10) — renders the real THUMBNAIL of the migrated file (a presigned image/video URL),
 * mirroring the legacy card, and opens a full-size **lightbox** on click. `media.url` is null when storage is unconfigured —
 * then we fall back to a filename chip (no thumbnail to show).
 *
 * The annotate ("Bild Bearbeiten") action is live for an EDITABLE image caller (ADR-025 A3a): pass `canEdit` + an
 * `onSaveEditedImage` handler and the lightbox's t("manualEditor.editImage") opens the in-house {@link ImageAnnotateEditor}, whose Save lands
 * the edited PNG straight onto the step. A read-only caller (or one that supplies no save handler) gets the deferred
 * "coming soon" placeholder instead. Add-more ("Zusätzliche Bilder") is the in-house {@link ImageCollageEditor} (ADR-025 A3b),
 * live under the SAME gate plus a `session` (its asset picker needs one); without a session it stays the deferred placeholder.
 */
export function StepMedia({
  media,
  canEdit = false,
  onSaveEditedImage,
  onCutVideo,
  session,
  manualId = null,
  thumbClassName,
}: {
  media: ManualMediaRef;
  /** Size/shape of the thumbnail button — the step card's media column wants a bigger tile than the default 32×20. */
  thumbClassName?: string;
  /** The caller is in an editable context (draft + manage) — gates the live annotate affordance with `onSaveEditedImage`. */
  canEdit?: boolean;
  /** Land the edited PNG onto the step (the A3a edit-attach write). When present AND `canEdit`, t("manualEditor.editImage") goes live. */
  onSaveEditedImage?: (file: File) => Promise<unknown>;
  /** Queue an async VIDEO cut (ADR-025 A4). When present AND `canEdit` on a video, the lightbox's image actions are replaced
   *  by a live t("manualEditor.cutVideo") that opens the {@link VideoTrimEditor}; otherwise the video keeps a deferred placeholder. */
  onCutVideo?: (input: CutVideoInput) => Promise<unknown>;
  /** The auth session — the collage editor's asset picker needs one. When present (with `canEdit` + `onSaveEditedImage` on an
   *  image), t("manualEditor.addMoreImages") goes live; otherwise it stays the deferred placeholder. */
  session?: AuthSession | null;
  /** The manual in context (#217 P2) — threaded to the collage picker so its asset drawer offers the "This manual" scope. */
  manualId?: number | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [collaging, setCollaging] = useState(false);
  const [cutting, setCutting] = useState(false);
  const isImage = media.kind === "image" && media.url != null;
  const isVideo = media.kind === "video" && media.url != null;
  const label = media.name ?? t("manualEditor.attachedMedia");

  // No presigned URL (storage unconfigured) → a plain filename chip, nothing to preview.
  if (!media.url) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
        <FileQuestion aria-hidden="true" className="size-3.5" />
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("manualEditor.previewLabel", { label: label })}
        className={cn(
          "group/thumb border-border bg-muted focus-visible:ring-ring/50 relative block shrink-0 cursor-pointer overflow-hidden rounded-md border-0 p-0 outline-none focus-visible:ring-2",
          thumbClassName ?? "h-20 w-32",
        )}
      >
        {isImage ? (
          <img src={media.url} alt={label} className="h-full w-full object-cover" />
        ) : isVideo ? (
          <>
            <video src={media.url} className="h-full w-full object-cover" muted preload="metadata" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25">
              <Play aria-hidden="true" className="size-6 text-white/90" />
            </span>
          </>
        ) : (
          <span className="flex h-full items-center justify-center">
            <FileQuestion aria-hidden="true" className="text-muted-foreground size-6" />
          </span>
        )}
        {/* Filename overlay (legacy card style) + a hover veil. */}
        <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-0.5 text-[0.65rem] text-white">{label}</span>
        <span className="absolute inset-0 bg-black/0 transition-colors group-hover/thumb:bg-black/10" aria-hidden="true" />
      </button>

      {open ? (
        <MediaLightbox
          media={media}
          label={label}
          isImage={isImage}
          isVideo={isVideo}
          onClose={() => setOpen(false)}
          // Entering an editor CLOSES the lightbox (they're siblings, never nested) so closing the editor returns to the
          // manual — not to a stale lightbox underneath — and there's no nested-modal focus/dismiss bounce. Live only for an
          // EDITABLE image caller with a save handler (A3a); otherwise `undefined` → the lightbox shows the deferred button.
          onEdit={isImage && canEdit && onSaveEditedImage ? () => { setOpen(false); setEditing(true); } : undefined}
          // t("manualEditor.addMoreImages") (collage, A3b) — live under the SAME gate plus a session (its asset picker needs one). Same
          // sibling-not-nested pattern as Edit; absent (read-only / no session) → the lightbox keeps the deferred placeholder.
          onAddImages={isImage && canEdit && onSaveEditedImage && session != null ? () => { setOpen(false); setCollaging(true); } : undefined}
          // t("manualEditor.cutVideo") (A4) — for an editable VIDEO with a cut handler; opens the trim editor (sibling, not nested).
          onCut={isVideo && canEdit && onCutVideo ? () => { setOpen(false); setCutting(true); } : undefined}
        />
      ) : null}
      {cutting && onCutVideo ? (
        <VideoTrimEditor
          media={media}
          onCut={onCutVideo}
          onClose={() => setCutting(false)}
          // A cut was queued → close the trimmer and return to the manual (the trimmed clip appears on the next body refresh;
          // no poll/WS yet). NOT the re-open-the-lightbox contract the image editors use — the result isn't ready synchronously.
          onSaved={() => setCutting(false)}
        />
      ) : null}
      {editing && onSaveEditedImage ? (
        <ImageAnnotateEditor
          media={media}
          onSave={onSaveEditedImage}
          onClose={() => setEditing(false)}
          // On a successful save, close the editor and re-open the LIGHTBOX on the (now-updated) image — the author sees the
          // result, then closes it to return to the manual or clicks t("manualEditor.editImage") again. (Cancel/X just returns to the manual.)
          onSaved={() => {
            setEditing(false);
            setOpen(true);
          }}
        />
      ) : null}
      {collaging && onSaveEditedImage && session != null ? (
        <ImageCollageEditor
          media={media}
          session={session}
          manualId={manualId}
          onSave={onSaveEditedImage}
          onClose={() => setCollaging(false)}
          // Same re-open-the-lightbox-on-success contract as the annotate editor (the author sees the combined result).
          onSaved={() => {
            setCollaging(false);
            setOpen(true);
          }}
        />
      ) : null}
    </>
  );
}

/** The full-size media preview (the legacy lightbox). Edit / Add-more are the upload-path features (FS Phase 6 / Phase 11) —
 *  disabled placeholders here so the intent is visible without promising an action that can't yet run. */
function MediaLightbox({
  media,
  label,
  isImage,
  isVideo,
  onClose,
  onEdit,
  onAddImages,
  onCut,
}: {
  media: ManualMediaRef;
  label: string;
  isImage: boolean;
  isVideo: boolean;
  onClose: () => void;
  onEdit?: () => void;
  /** Live t("manualEditor.addMoreImages") (collage, A3b) — opens the {@link ImageCollageEditor}. Absent ⇒ the deferred placeholder. */
  onAddImages?: () => void;
  /** Live t("manualEditor.cutVideo") (A4) — opens the {@link VideoTrimEditor}. Absent (read-only / no handler) ⇒ the deferred placeholder. */
  onCut?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onClose()} className="fixed inset-0 z-[60] flex flex-col bg-black/80 p-6">
      <Modal className="flex min-h-0 w-full flex-1 flex-col outline-hidden">
        <Dialog aria-label={t("manualEditor.previewLabel2", { label: label })} className="flex min-h-0 flex-1 flex-col outline-none">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="truncate text-sm font-medium text-white">{label}</span>
            <Button variant="ghost" size="icon-lg" aria-label={t("manualEditor.closePreview")} onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
              <X aria-hidden="true" />
            </Button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center">
            {isImage ? (
              <img src={media.url!} alt={label} className="max-h-full max-w-full object-contain" />
            ) : isVideo ? (
              <video src={media.url!} controls className="max-h-full max-w-full" />
            ) : (
              <p className="text-white/80">{t("manualEditor.previewUnsupported")}</p>
            )}
          </div>
          {/* A VIDEO gets the trim/cut action (ADR-025 A4) — never the image tools; an IMAGE gets Edit (annotate, A3a) +
              Add-more (collage, A3b). Each is live only when the caller passed the matching handler (editable + a save/cut
              handler); otherwise a deferred placeholder keeps the intent visible. */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {isVideo ? (
              onCut ? (
                <Button size="sm" onPress={onCut} className="gap-1.5">
                  <Scissors aria-hidden="true" className="size-3.5" />
                  {t("manualEditor.cutVideo")}
                </Button>
              ) : (
                <DeferredActionButton
                  label={t("manualEditor.cutVideo")}
                  tooltip={t("manualEditor.videoTrimmingSoon")}
                  variant="outline"
                  className="border-white/40 bg-transparent text-white"
                  icon={<Scissors aria-hidden="true" className="size-3.5" />}
                />
              )
            ) : (
              <>
                {onEdit ? (
                  <Button variant="outline" size="sm" onPress={onEdit} className="gap-1.5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white dark:border-white/40 dark:bg-transparent dark:hover:bg-white/10">
                    <Pencil aria-hidden="true" className="size-3.5" />
                    {t("manualEditor.editImage")}
                  </Button>
                ) : (
                  <DeferredActionButton
                    label={t("manualEditor.editImage")}
                    tooltip={isImage ? t("manualEditor.editingImagesComingSoon") : t("manualEditor.onlyImagesCanBeAnnotated")}
                    // Transparent + white on the fixed black overlay (reads in both themes).
                    variant="outline"
                    className="border-white/40 bg-transparent text-white"
                    icon={<Pencil aria-hidden="true" className="size-3.5" />}
                  />
                )}
                {onAddImages ? (
                  <Button variant="outline" size="sm" onPress={onAddImages} className="gap-1.5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white dark:border-white/40 dark:bg-transparent dark:hover:bg-white/10">
                    <ImagePlus aria-hidden="true" className="size-3.5" />
                    {t("manualEditor.addMoreImages")}
                  </Button>
                ) : (
                  <DeferredActionButton
                    label={t("manualEditor.addMoreImages")}
                    tooltip={isImage ? t("manualEditor.additionalImagesComingSoon") : t("manualEditor.onlyImagesCanBeCombined")}
                    variant="outline"
                    // On the fixed black lightbox overlay: a transparent surface + white text/border read in BOTH themes (the
                    // outline variant's default `bg-background` would paint white-on-white in light mode).
                    className="border-white/40 bg-transparent text-white"
                    icon={<ImagePlus aria-hidden="true" className="size-3.5" />}
                  />
                )}
              </>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
